/**
 * Hook to manage funnel editor guided tour
 * Triggers when a funnel is newly created with no design configured
 */

import { useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useNextStep } from 'nextstepjs';
import toast from 'react-hot-toast';
import type { FunnelNode } from '../types';

const TOUR_SHOWN_KEY = 'funnel_editor_tour_shown';

/**
 * Continuously trigger resize events to keep tour highlight aligned
 * Stops when the interval is cleared
 */
function startContinuousReposition(): () => void {
  const intervalId = setInterval(() => {
    window.dispatchEvent(new Event('resize'));
  }, 100); // Trigger every 100ms
  
  return () => clearInterval(intervalId);
}

interface UseFunnelEditorTourOptions {
  funnelId: string | undefined;
  nodes: FunnelNode[] | undefined;
  isLoading: boolean;
  onResetView?: () => void; // Callback to reset canvas zoom/position
  onSelectFirstStep?: () => void; // Callback to select the first step
  onSave?: () => void; // Callback to trigger save
  onShowPreviewButton?: () => void; // Callback to show the preview button (simulate hover)
  hasDesignSelected?: () => boolean; // Check if design is selected for the current step
}

/**
 * Template-specific tips shown at the end of the tour
 */
const TEMPLATE_TIPS: Record<string, { title: string; tips: string[] }> = {
  'default': {
    title: 'Default Checkout',
    tips: [
      'Customize your checkout design in the editor panel',
      'Add custom fields, upsells, or modify the layout as needed',
    ],
  },
  'shopify-default': {
    title: 'Shopify Checkout',
    tips: [
      'The Shopify step syncs products and cart from your store',
      'Customers land on Shopify first, then proceed to checkout',
      'Make sure your Shopify integration is properly configured',
    ],
  },
  'upsell-default': {
    title: 'Checkout with Upsell',
    tips: [
      'Configure the post-purchase offer in the Upsell step',
      'Set compelling offers to increase average order value',
      'The upsell appears after successful checkout, before Thank You',
    ],
  },
  'geo-targeted': {
    title: 'Geo-Targeted Flow',
    tips: [
      'Configure country conditions to route customers to different offers',
      'Each region can have a unique checkout experience',
      'Click on condition edges to modify routing rules',
    ],
  },
};

interface UseFunnelEditorTourReturn {
  onTourComplete: (tourName: string | null) => void;
  onTourSkip: (step: number, tourName: string | null) => void;
  onTourStepChange: (step: number, tourName: string | null) => void;
}

/**
 * Check if any step has a design variant configured
 * Note: instanceId is the plugin instance, variant is the actual design choice
 */
function hasAnyDesignConfigured(nodes: FunnelNode[] | undefined): boolean {
  if (!nodes || nodes.length === 0) return false;

  return nodes.some((node) => {
    // Skip Shopify storefront nodes (they don't need design selection)
    if (node.type === 'storefront_shopify') return true;

    // Skip condition nodes (they don't have designs)
    if (node.kind === 'condition') return true;

    // Check if node has a design variant selected (not just instanceId)
    // The variant field is what indicates a design choice was made
    if (node.config?.variant) return true;

    // Check variants for split steps (A/B testing)
    if (node.variants && node.variants.length > 0) {
      return node.variants.some((variant: any) => variant.config?.variant);
    }

    return false;
  });
}

/**
 * Check if tour was already shown for this funnel
 */
function wasTourShown(funnelId: string): boolean {
  try {
    const shown = localStorage.getItem(`${TOUR_SHOWN_KEY}_${funnelId}`);
    return shown === 'true';
  } catch {
    return false;
  }
}

/**
 * Mark tour as shown for this funnel
 */
function markTourAsShown(funnelId: string): void {
  try {
    localStorage.setItem(`${TOUR_SHOWN_KEY}_${funnelId}`, 'true');
  } catch {
    // Ignore localStorage errors
  }
}

/**
 * Hook to trigger the funnel editor setup tour
 * 
 * Triggers when:
 * - Funnel has no design configured in any step
 * - Tour hasn't been shown for this funnel yet
 * 
 * Also triggers immediately if URL has ?tour=setup param (from funnel creation)
 */
export function useFunnelEditorTour({
  funnelId,
  nodes,
  isLoading,
  onResetView,
  onSelectFirstStep,
  onSave,
  onShowPreviewButton,
  hasDesignSelected,
}: UseFunnelEditorTourOptions): UseFunnelEditorTourReturn {
  const [searchParams, setSearchParams] = useSearchParams();
  const { startNextStep } = useNextStep();
  const hasStartedTour = useRef(false);
  const stopRepositionRef = useRef<(() => void) | null>(null);
  const currentStepRef = useRef<number>(0);

  // Effect to manage "design required" state for disabling Next button
  useEffect(() => {
    const checkDesignAndUpdateUI = () => {
      // Only apply on design step (step 3 in 1-indexed)
      const onDesignStep = currentStepRef.current === 3;
      const designSelected = hasDesignSelected ? hasDesignSelected() : true;
      
      if (onDesignStep && !designSelected) {
        document.body.setAttribute('data-tour-design-required', 'true');
      } else {
        document.body.removeAttribute('data-tour-design-required');
      }
    };

    // Check immediately and on interval while tour is active
    const intervalId = setInterval(checkDesignAndUpdateUI, 200);
    checkDesignAndUpdateUI();

    return () => {
      clearInterval(intervalId);
      document.body.removeAttribute('data-tour-design-required');
    };
  }, [hasDesignSelected]);

  useEffect(() => {
    // Don't do anything while loading
    if (isLoading) return;

    // Need a funnel ID
    if (!funnelId) return;

    // Need nodes to check design status
    if (!nodes) return;

    // Prevent running multiple times
    if (hasStartedTour.current) return;

    // Check if tour was already shown for this funnel
    if (wasTourShown(funnelId)) {
      // Remove the tour param from URL if present
      if (searchParams.get('tour') === 'setup') {
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('tour');
        setSearchParams(newParams, { replace: true });
      }
      return;
    }

    // Check if any step has a design configured
    const hasDesign = hasAnyDesignConfigured(nodes);


    // Only show tour if no design is configured
    if (hasDesign) {
      // Remove the tour param from URL if present
      if (searchParams.get('tour') === 'setup') {
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('tour');
        setSearchParams(newParams, { replace: true });
      }
      return;
    }

    // Start the tour - no design configured and not shown yet
    hasStartedTour.current = true;

    // Small delay to let the page render, then reset view and start tour
    const timer = setTimeout(() => {
      
      // Reset canvas view to ensure proper positioning for tour (instant, no animation)
      if (onResetView) {
        onResetView();
      }
      
      // Small delay for React to settle after fitView, then start tour
      setTimeout(() => {
        startNextStep('funnel-editor-setup');
        
        // Start continuous repositioning to keep highlight aligned with React Flow transforms
        stopRepositionRef.current = startContinuousReposition();
      }, 150);
    }, 800);

    return () => clearTimeout(timer);
  }, [funnelId, nodes, isLoading, searchParams, setSearchParams, startNextStep, onResetView]);

  /**
   * Stop continuous repositioning
   */
  const stopReposition = useCallback(() => {
    if (stopRepositionRef.current) {
      stopRepositionRef.current();
      stopRepositionRef.current = null;
    }
  }, []);

  /**
   * Hide the preview button (cleanup when tour ends)
   */
  const hidePreviewButton = useCallback(() => {
    window.dispatchEvent(new CustomEvent('funnel-editor-hide-preview-button'));
  }, []);

  /**
   * Clean up tour UI state
   */
  const cleanupTourUI = useCallback(() => {
    document.body.removeAttribute('data-tour-design-required');
    currentStepRef.current = 0;
  }, []);

  /**
   * Handle tour completion
   */
  const onTourComplete = useCallback((tourName: string | null) => {
    if (tourName === 'funnel-editor-setup' && funnelId) {
      stopReposition();
      hidePreviewButton();
      cleanupTourUI();
      markTourAsShown(funnelId);

      // Get template ID from URL and show template-specific tips
      const templateId = searchParams.get('template');
      if (templateId && TEMPLATE_TIPS[templateId]) {
        const tips = TEMPLATE_TIPS[templateId];
        // Show tips as a toast notification
        const tipsText = `${tips.title} - Quick Tips:\n• ${tips.tips.join('\n• ')}`;
        toast.success(tipsText, { duration: 8000 });
      }

      // Remove the tour and template params from URL
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('tour');
      newParams.delete('template');
      setSearchParams(newParams, { replace: true });
    }
  }, [funnelId, searchParams, setSearchParams, stopReposition, hidePreviewButton, cleanupTourUI]);

  /**
   * Handle tour skip
   */
  const onTourSkip = useCallback((step: number, tourName: string | null) => {
    if (tourName === 'funnel-editor-setup' && funnelId) {
      stopReposition();
      hidePreviewButton();
      cleanupTourUI();
      markTourAsShown(funnelId);

      // Remove the tour and template params from URL
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('tour');
      newParams.delete('template');
      setSearchParams(newParams, { replace: true });
    }
  }, [funnelId, searchParams, setSearchParams, stopReposition, hidePreviewButton, cleanupTourUI]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopReposition();
    };
  }, [stopReposition]);

  const { closeNextStep, setCurrentStep } = useNextStep();

  /**
   * Handle tour step change
   * Auto-click on elements when advancing to certain steps
   */
  const onTourStepChange = useCallback((step: number, tourName: string | null) => {
    if (tourName !== 'funnel-editor-setup') return;

    currentStepRef.current = step;

    // Step 1 (0-indexed) or Step 2 (1-indexed) = "Choose a Template" - auto-select first step to open editor
    // When user clicks "Next" on first step, select the step to open the editor panel
    if ((step === 1 || step === 2) && onSelectFirstStep) {
      onSelectFirstStep();
    }

    // Step 3 (0-indexed) or Step 4 (1-indexed) = "Save Your Changes" - validate design is selected
    // If no design selected, show toast and go back to design step
    if (step === 3 || step === 4) {
      if (hasDesignSelected && !hasDesignSelected()) {
        toast.error('Please select a design before saving');
        // Go back to design step (step 3 for 1-indexed which nextstepjs uses)
        setTimeout(() => {
          setCurrentStep(3); // Design step in 1-indexed
        }, 100);
        return;
      }
    }

    // Step 4 (0-indexed) or Step 5 (1-indexed) = "Preview Your Checkout" - auto-save and show preview button
    // When user clicks "Next" on save step, trigger save and show the preview button
    if ((step === 4 || step === 5)) {
      if (onSave) {
        onSave();
      }
      if (onShowPreviewButton) {
        // Small delay to let save complete visually
        setTimeout(() => {
          onShowPreviewButton();
        }, 300);
      }
    }
  }, [onSelectFirstStep, onSave, onShowPreviewButton, hasDesignSelected, setCurrentStep]);

  return {
    onTourComplete,
    onTourSkip,
    onTourStepChange,
  };
}

